import { useEffect, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { Lock, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import usePageTitle from "@/lib/usePageTitle";

export default function AdminLogin() {
  usePageTitle("Admin Login");
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user && typeof user === "object") navigate("/admin", { replace: true });
  }, [user, navigate]);

  if (user && typeof user === "object") return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back.");
      navigate("/admin", { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen flex flex-col bg-[#F8F7F4]">
      <div className="max-w-[1400px] w-full mx-auto px-6 md:px-12 py-6">
        <Link to="/" className="flex flex-col leading-none w-fit">
          <span className="font-serif-display text-2xl tracking-[0.18em] uppercase">GESL</span>
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5A56] mt-0.5">Spinners Limited</span>
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 pb-20">
        <form
          data-testid="login-form"
          onSubmit={onSubmit}
          className="w-full max-w-md bg-[#F8F7F4] border border-[#1A1A1A]/10 p-10 md:p-12"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#2E4A3D] text-[#F8F7F4] flex items-center justify-center">
              <Lock size={18} />
            </div>
            <div>
              <div className="overline">Admin Console</div>
              <h1 className="font-serif-display text-2xl mt-1">Sign in</h1>
            </div>
          </div>
          <p className="text-sm text-[#5C5A56] mb-8">
            Restricted area for GESL Spinners Limited administrators.
          </p>

          <label className="block text-[10px] tracking-[0.25em] uppercase text-[#5C5A56] mb-2 font-semibold">
            Email
          </label>
          <input
            data-testid="login-email"
            type="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input-editorial mb-6"
            placeholder="admin@geslspinners.com"
          />

          <label className="block text-[10px] tracking-[0.25em] uppercase text-[#5C5A56] mb-2 font-semibold">
            Password
          </label>
          <input
            data-testid="login-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input-editorial mb-8"
            placeholder="••••••••"
          />

          <button
            data-testid="login-submit"
            type="submit"
            disabled={submitting}
            className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? <><Loader2 size={16} className="animate-spin" /> Signing in…</> : <>Sign in <ArrowUpRight size={16} /></>}
          </button>

          <div className="mt-8 text-xs text-[#5C5A56]/80 text-center">
            <Link to="/" className="link-underline">← Back to website</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
